import { useCallback } from 'react';

const LOGOUT_MARKER_KEY = 'farmshare-logout-marker';

/**
 * Custom hook that provides functions to manage Apollo cache cleanup during auth transitions.
 *
 * This is useful for clearing cached data when a user logs out and logs back in
 * with a different account, when the token is refreshed, or when switching vendors.
 * @returns Object with cleanup functions and logout marker utilities
 */
export function useAuthStorageCleanup() {
  const cleanupApolloCache = useCallback(() => {
    const keysToRemove: string[] = [];

    for (const key in window.localStorage) {
      if (key.startsWith('apollo-cache-persist')) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => {
      window.localStorage.removeItem(key);
    });

    return keysToRemove.length;
  }, []);

  const markLogout = useCallback(() => {
    localStorage.setItem(LOGOUT_MARKER_KEY, Date.now().toString());
  }, []);

  const checkAndClearLogoutMarker = useCallback(() => {
    const logoutMarker = localStorage.getItem(LOGOUT_MARKER_KEY);
    if (logoutMarker) {
      localStorage.removeItem(LOGOUT_MARKER_KEY);
      cleanupApolloCache();
      return true;
    }
    return false;
  }, [cleanupApolloCache]);

  return {
    cleanupApolloCache,
    markLogout,
    checkAndClearLogoutMarker,
  };
}
